// src/admin/AdminLayout.js
import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const SUPER_ADMIN_EMAILS = [
  'matthew@backpass.co',
  'mgriff7@gmail.com',
  'mhgbackpass@gmail.com',
  'mhgcommercial@gmail.com'
];

function AdminLayout() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [permissions, setPermissions] = useState({});
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size and update on resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Set viewport meta tag for better mobile zoom control
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      // Allow zooming out further and better control
      viewport.content = "width=device-width, initial-scale=0.5, minimum-scale=0.3, maximum-scale=5.0, user-scalable=yes";
    }
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile]);

  useEffect(() => {
    const loadPermissions = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        // Check if super admin
        if (SUPER_ADMIN_EMAILS.includes(currentUser.email?.toLowerCase())) {
          setIsSuperAdmin(true);
          setPermissions({
            journeys: true,
            routes: true,
            revisions: true,
            hostels: true,
            tips: true,
            'explore-cards': true,
            cities: true,
            users: true,
            'terms': true,
            'privacy': true,
            permissions: true,
            'exchange-rates': true,
            pricing: true,
            transactions: true,
            bookings: true,
          });
        } else {
          // Load permissions from Firestore
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setPermissions(userData.adminPermissions || {});
          }
        }
      } catch (error) {
        console.error('Error loading permissions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, [currentUser]);

  // Check if current route is allowed
  useEffect(() => {
    if (!loading) {
      const currentPath = location.pathname.split('/').pop();
      const isAdminRoot = location.pathname === '/admin' || location.pathname === '/admin/';
      
      // Map routes to permissions
      const routePermissions = {
        'bookings': 'bookings',
        'journeys': 'journeys',
        'routes': 'routes',
        'revisions': 'revisions',
        'hostels': 'hostels',
        'tips': 'tips',
        'explore-cards': 'explore-cards',
        'cities': 'cities',
        'users': 'users',
        'terms': 'terms',
        'privacy': 'privacy',
        'permissions': 'permissions',
        'pricing-calendar': 'pricing',
        'transactions': 'transactions',
        'sample-data': 'pricing',
        'exchange-rates': 'exchange-rates'
      };

      // If on a specific page, check permission
      if (!isAdminRoot && currentPath && routePermissions[currentPath]) {
        const requiredPermission = routePermissions[currentPath];
        const hasPermission = permissions[requiredPermission] === true;
        
        if (!hasPermission) {
          // Redirect to first available page
          const availablePages = Object.entries(routePermissions)
            .filter(([route, perm]) => permissions[perm] === true)
            .map(([route]) => route);
          
          if (availablePages.length > 0) {
            navigate(`/admin/${availablePages[0]}`, { replace: true });
          } else {
            // No permissions at all - shouldn't happen due to AdminRoute check
            navigate('/', { replace: true });
          }
        }
      }
      
      // If at admin root, redirect to first available page
      if (isAdminRoot) {
        const availablePages = ['bookings', 'journeys', 'routes', 'revisions', 'hostels', 'pricing-calendar', 'transactions', 'sample-data', 'explore-cards', 'cities', 'users', 'terms', 'privacy', 'permissions']
          .filter(page => {
            const permissionKey = page === 'explore-cards' ? 'explore-cards' :
                                page === 'pricing-calendar' ? 'pricing' :
                                page === 'transactions' ? 'transactions' :
                                page === 'sample-data' ? 'pricing' : page;
            return permissions[permissionKey] === true;
          });
        
        if (availablePages.length > 0) {
          navigate(`/admin/${availablePages[0]}`, { replace: true });
        }
      }
    }
  }, [loading, location, permissions, navigate]);

  const handleLogout = async () => {
    if (!currentUser) return;
    await currentUser?.auth?.signOut();
    navigate('/signin');
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading admin panel...</div>;
  }

  const navItems = [
    { path: 'bookings', label: 'Manage Bookings', permission: 'bookings' },
    { path: 'journeys', label: 'Manage Journeys', permission: 'journeys' },
    { path: 'routes', label: 'Manage Routes', permission: 'routes' },
    { path: 'revisions', label: 'Route Revisions', permission: 'revisions' },
    { path: 'hostels', label: 'Manage Hostels', permission: 'hostels' },
    { path: 'tips', label: 'Tips & Feedback', permission: 'tips' },
    { path: 'pricing-calendar', label: 'Pricing Calendar', permission: 'pricing' },
    { path: 'transactions', label: 'User Transactions', permission: 'transactions' },
    { path: 'sample-data', label: 'Generate Sample Data', permission: 'pricing' },
    { path: 'explore-cards', label: 'Manage Explore Cards', permission: 'explore-cards' },
    { path: 'cities', label: 'Manage Cities', permission: 'cities' },
    { path: 'users', label: 'Edit User Details', permission: 'users' },
    { path: 'terms', label: 'Terms & Conditions', permission: 'terms' },
    { path: 'privacy', label: 'Privacy Policy', permission: 'privacy' },
    { path: 'exchange-rates', label: 'Exchange Rates', permission: 'exchange-rates' },
    { path: 'permissions', label: 'User Permissions', permission: 'permissions' }
  ];

  return (
    <>
      {/* Add global mobile viewport styles */}
      {isMobile && (
        <style>{`
          html { 
            margin: 0; 
            padding: 0; 
            overflow: auto;
            width: 100%;
            height: 100%;
          }
          body { 
            margin: 0;
            padding: 15px;
            background: #f0f0f0;
            min-width: 100vw;
            overflow-x: auto;
            overflow-y: auto;
          }
          #root {
            max-width: 900px;
            margin: 0 auto;
            background: white;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            overflow: visible;
            min-height: 100vh;
          }
          * { 
            box-sizing: border-box; 
          }
          /* Make forms and inputs more mobile-friendly */
          input, select, textarea {
            font-size: 16px !important; /* Prevents zoom on focus in iOS */
          }
          /* Better scrolling */
          .admin-main-content {
            -webkit-overflow-scrolling: touch;
          }
        `}</style>
      )}
      
      {/* Mobile Menu Button - Always visible on mobile */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        style={{
          position: 'fixed',
          top: '8px',
          left: '8px',
          zIndex: 1001,
          padding: '8px',
          backgroundColor: '#2B2B2B',
          color: 'white',
          border: 'none',
          borderRadius: '3px',
          cursor: 'pointer',
          fontSize: '16px',
          display: isMobile ? 'block' : 'none',
          minWidth: '40px',
          minHeight: '40px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
        }}
      >
        {isMobileMenuOpen ? '✕' : '☰'}
      </button>

      <div style={{ 
        display: 'flex', 
        minHeight: '100vh',
        position: 'relative',
        width: '100%',
        maxWidth: isMobile ? '100%' : 'none',
        margin: '0',
        overflow: 'visible',
        backgroundColor: isMobile ? 'transparent' : 'white'
      }}>
        {/* === SIDEBAR === */}
        <aside
          style={{
            width: isMobile ? (isMobileMenuOpen ? '100%' : '0') : '200px',
            backgroundColor: '#2B2B2B',
            padding: isMobile ? (isMobileMenuOpen ? '50px 15px 30px' : '0') : '20px',
            color: 'white',
            position: isMobile ? 'fixed' : 'static',
            top: 0,
            left: 0,
            height: isMobile ? '100vh' : 'auto',
            maxHeight: isMobile ? '100vh' : 'none',
            zIndex: 1000,
            overflowY: isMobile ? 'auto' : 'hidden',
            overflowX: 'hidden',
            transition: 'all 0.3s ease',
            transform: isMobile ? (isMobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {(!isMobile || isMobileMenuOpen) && (
            <>
              <h3 style={{ 
                fontSize: isMobile ? '16px' : '20px',
                marginBottom: isMobile ? '12px' : '20px'
              }}>
                Admin Menu
              </h3>
              {currentUser && (
                <div style={{ 
                  fontSize: isMobile ? '10px' : '12px', 
                  color: '#aaa', 
                  marginBottom: isMobile ? '12px' : '20px',
                  paddingBottom: isMobile ? '12px' : '20px',
                  borderBottom: '1px solid #444'
                }}>
                  {currentUser.email}
                  {isSuperAdmin && (
                    <div style={{ 
                      marginTop: '5px',
                      color: '#4CAF50',
                      fontWeight: 'bold',
                      fontSize: isMobile ? '11px' : '12px'
                    }}>
                      Super Admin
                    </div>
                  )}
                </div>
              )}
              <nav style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: isMobile ? '8px' : '10px',
                paddingLeft: isMobile ? '2px' : '0',
                paddingRight: isMobile ? '10px' : '0'
              }}>
                {navItems.map(item => {
                  // Check if user has permission for this item
                  const hasPermission = permissions[item.permission] === true;

                  // Additional check: Only super admins can see terms and privacy
                  if ((item.permission === 'terms' || item.permission === 'privacy') && !isSuperAdmin) return null;

                  if (!hasPermission) return null;

                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => isMobile && setIsMobileMenuOpen(false)}
                      style={({ isActive }) => ({
                        color: isActive ? '#4CAF50' : 'white',
                        textDecoration: 'none',
                        padding: isMobile ? '8px 10px' : '8px 12px',
                        borderRadius: '3px',
                        backgroundColor: isActive ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                        transition: 'all 0.2s',
                        fontSize: isMobile ? '13px' : '14px',
                        minHeight: isMobile ? '36px' : 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        textAlign: 'left',
                        justifyContent: 'flex-start'
                      })}
                    >
                      {item.label}
                    </NavLink>
                  );
                })}
                <button
                  onClick={handleLogout}
                  style={{
                    marginTop: isMobile ? '15px' : '20px',
                    padding: isMobile ? '10px 12px' : '10px',
                    backgroundColor: '#800020',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '14px' : '14px',
                    minHeight: isMobile ? '40px' : 'auto',
                    marginLeft: isMobile ? '3px' : '0',
                    marginRight: isMobile ? '12px' : '0'
                  }}
                >
                  Logout
                </button>
              </nav>
            </>
          )}
        </aside>
        
        {/* Mobile Overlay */}
        {isMobile && isMobileMenuOpen && (
          <div
            onClick={() => setIsMobileMenuOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999
            }}
          />
        )}

        {/* === MAIN AREA === */}
        <main 
          className="admin-main-content"
          style={{ 
            flex: 1, 
            backgroundColor: 'white', 
            padding: isMobile ? '55px 10px 20px 10px' : '20px', 
            color: '#333333',
            width: '100%',
            overflow: 'auto',
            boxSizing: 'border-box',
            position: 'relative'
          }}
        >
          <div style={{
            width: '100%',
            maxWidth: isMobile ? '800px' : '100%',
            margin: isMobile ? '0 auto' : '0',
            boxSizing: 'border-box',
            paddingBottom: isMobile ? '40px' : '0'
          }}>
            <Outlet />
          </div>
        </main>
      </div>
    </>
  );
}

export default AdminLayout;